/**
 * Dashboard /esims tab — eSIM grid with reserved/active cards, countdown
 * ticker, action handlers (cancel/archive/QR), and the top-up-return
 * query handler that flips reserved → active after NowPayments redirect.
 *
 * Originally lines 150-441 + 1293-1422 of `routes/dashboard.ts`. Phase 2.2.
 */
import { Auth } from '../../auth';
import { escapeHtml } from '../../utils';
import * as api from '../../api';
import { trialEnabled } from '../../trial-cta';
import { showToast } from '../../toast';
import { t } from '../../i18n';
import { deriveEsimStatus } from '../../utils/esim-status';
import { fmtData, fmtMoney } from '../../utils/format';
import { icon } from '../../utils/icons';
import { countryDisplayName } from '../../utils/country-name';

/**
 * Format the reservation countdown. Returns null if the deadline is past.
 * Format: "Mm Ss" or "Hh Mm Ss" — short, no localised number formatting.
 */
export function formatRemaining(reservedUntil: string): string | null {
  const ms = new Date(reservedUntil).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

/**
 * User-facing eSIM ID. Operator decision 2026-05-08: show MSISDN — that's
 * the same number the RSP propagates into the on-device eSIM label, so
 * users can match what they see in iOS/Android settings to what we show
 * in the dashboard. Word "MSISDN" never appears in user UI; field label
 * is "eSIM ID" / "ID eSIM" via the existing dash.esims.idLabel key.
 *
 * Returns { display, copyable }:
 *   - display:  string to render — MSISDN if assigned, else "Pending…"
 *               (i18n via dash.esims.idPending in 5 locales)
 *   - copyable: string to put in click-to-copy attributes — null when
 *               we have nothing real to copy yet (the copy button is
 *               then suppressed in render).
 */
export function userFacingEsimId(e: api.EsimResp): { display: string; copyable: string | null } {
  if (e.msisdn) return { display: e.msisdn, copyable: e.msisdn };
  return { display: t('dash.esims.idPending'), copyable: null };
}

export function reservedCardHtml(e: api.EsimResp): string {
  const idView = userFacingEsimId(e);
  const remaining = e.reservedUntil ? formatRemaining(e.reservedUntil) : null;
  return `<article class="dash-esim-card dash-esim-card--reserved" data-esim-id="${e.id}" data-esim-reserved-until="${escapeHtml(e.reservedUntil ?? '')}">
  <header class="dash-esim-card__head">
    <span class="dash-esim-card__flag" aria-hidden="true">${icon('hourglass', 30)}</span>
    <div class="dash-esim-card__title">
      <h3 class="dash-esim-card__country">${escapeHtml(t('dash.esims.reservedTitle'))}</h3>
      <p class="dash-esim-card__rate" data-i18n="dash.esims.global">${escapeHtml(t('dash.esims.global'))}</p>
    </div>
    <span class="dash-esim-card__status dash-esim-card__status--reserved" data-i18n="dash.esims.statusReserved">${escapeHtml(t('dash.esims.statusReserved'))}</span>
  </header>
  <div class="dash-esim-card__hero">
    <div class="dash-esim-card__balance">
      <span class="label" data-i18n="dash.esims.balance">${escapeHtml(t('dash.esims.balance'))}</span>
      <span class="dash-esim-card__balance-amount mono">${fmtMoney(e.balanceMicroUsdt)}</span>
    </div>
    <div class="dash-esim-card__id">
      <span class="label" data-i18n="dash.esims.idLabel">${escapeHtml(t('dash.esims.idLabel'))}</span>
      <span class="dash-esim-card__id-value mono"${idView.copyable ? ` title="${escapeHtml(t('dash.esims.idCopyHint'))}" data-esim-display-id="${escapeHtml(idView.copyable)}"` : ''}>${escapeHtml(idView.display)}${idView.copyable ? ` <button data-esim-display-id="${escapeHtml(idView.copyable)}" title="${escapeHtml(t('dash.esims.idCopyHint'))}" aria-label="${escapeHtml(t('dash.esims.idCopyHint'))}" class="dash-icon-btn">${icon('copy', 14)}</button>` : ''}</span>
    </div>
  </div>
  <div class="dash-esim-card__reservation">
    ${e.paymentInFlight ? '' : `<p class="dash-esim-card__reservation-msg" data-i18n="dash.esims.reservationHint">${escapeHtml(t('dash.esims.reservationHint'))}</p>`}
    ${e.paymentInFlight
      ? `<p class="dash-esim-card__reservation-countdown" data-i18n="dash.esims.paymentConfirming">${escapeHtml(t('dash.esims.paymentConfirming'))}</p>`
      : `<p class="dash-esim-card__reservation-countdown mono" data-esim-countdown>
      ${remaining ? `${escapeHtml(t('dash.esims.expiresIn'))} ${escapeHtml(remaining)}` : escapeHtml(t('dash.esims.reservationExpired'))}
    </p>`}
    ${e.pendingInvoiceUrl && remaining ? `
    <a class="btn btn--accent btn--full dash-esim-card__pay-now" href="${escapeHtml(e.pendingInvoiceUrl)}" target="_blank" rel="noopener noreferrer" style="margin-top:14px;display:block;text-align:center;">
      ${escapeHtml(t('dash.esims.payNow'))} →
    </a>
    ` : ''}
  </div>
</article>`;
}

export function activeCardHtml(e: api.EsimResp): string {
  // Country fields are legacy. Render every active card as a generic "Global
  // eSIM" so the UI matches the global-line product reality.
  // 2026-05-08: SPENT was previously `consumedMb × ratePerMicroUsdt`, which
  // returned $0.00 on global eSIMs because `ratePerMicroUsdt = 0` is set
  // per-eSIM (real per-country rates apply at the venta-sync layer). Now
  // backend ships `spentMicroUsdt` aggregated from `usage`-type ledger
  // rows; fall back to the old formula only on cached/legacy responses
  // that don't include the new field.
  const spentMicro = e.spentMicroUsdt !== undefined
    ? e.spentMicroUsdt
    : Math.round(e.consumedMb * e.ratePerMicroUsdt);
  // Single canonical status for the card body STATUS metric. Same
  // helper drives the details drawer — one label, never confusing.
  // Operator caught the three-different-statuses bug 2026-05-09;
  // the redundant DB-enum pill in the top-right is gone now too.
  const simStatus = deriveEsimStatus(e);
  const idView = userFacingEsimId(e);
  // User-editable label takes priority over generic "Global eSIM" when set.
  // E.g. "iPhone Marina" if the user named it. Click pencil → inline edit.
  const labelText = e.label && e.label.trim() ? e.label.trim() : t('dash.esims.global');
  return `<article class="dash-esim-card dash-esim-card--${e.status}" data-esim-id="${e.id}">
  <header class="dash-esim-card__head">
    <span class="dash-esim-card__flag" aria-hidden="true">${icon('globe', 30)}</span>
    <div class="dash-esim-card__title">
      <h3 class="dash-esim-card__country" data-esim-label-display="${escapeHtml(e.id)}">${escapeHtml(labelText)} <button data-esim-label-edit="${escapeHtml(e.id)}" title="${escapeHtml(t('dash.esims.renameHint'))}" aria-label="${escapeHtml(t('dash.esims.renameHint'))}" class="dash-icon-btn">${icon('pencil', 14)}</button></h3>
      <p class="dash-esim-card__rate" data-i18n="dash.esims.globalSub">${escapeHtml(t('dash.esims.globalSub'))}</p>
    </div>
    <button class="dash-sim-status dash-sim-status--${simStatus.key} dash-esim-card__status-pill" type="button" data-esim-status="${escapeHtml(e.id)}" title="${escapeHtml(t('dash.esims.simStatusHint'))}">
      <span class="dash-sim-status__dot" aria-hidden="true"></span>${escapeHtml(simStatus.label)}
    </button>
  </header>
  <div class="dash-esim-card__hero">
    <div class="dash-esim-card__balance">
      <span class="label" data-i18n="dash.esims.balance">${escapeHtml(t('dash.esims.balance'))}</span>
      <span class="dash-esim-card__balance-amount mono">${fmtMoney(e.balanceMicroUsdt)}</span>
    </div>
    <div class="dash-esim-card__id">
      <span class="label" data-i18n="dash.esims.idLabel">${escapeHtml(t('dash.esims.idLabel'))}</span>
      <span class="dash-esim-card__id-value mono"${idView.copyable ? ` title="${escapeHtml(t('dash.esims.idCopyHint'))}" data-esim-display-id="${escapeHtml(idView.copyable)}"` : ''}>${escapeHtml(idView.display)}${idView.copyable ? ` <button data-esim-display-id="${escapeHtml(idView.copyable)}" title="${escapeHtml(t('dash.esims.idCopyHint'))}" aria-label="${escapeHtml(t('dash.esims.idCopyHint'))}" class="dash-icon-btn">${icon('copy', 14)}</button>` : ''}</span>
    </div>
  </div>
  ${e.balanceMicroUsdt <= 0 ? `<p class="dash-esim-card__balance-empty" data-i18n="dash.esims.balanceEmpty">${escapeHtml(t('dash.esims.balanceEmpty'))}</p>` : ''}
  <div class="dash-esim-card__body dash-esim-card__body--two-col">
    <div class="dash-esim-card__metric">
      <span class="label" data-i18n="dash.esims.metricConsumed">${escapeHtml(t('dash.esims.metricConsumed'))}</span>
      <span class="mono">${fmtData(e.consumedMb)}</span>
    </div>
    <div class="dash-esim-card__metric">
      <span class="label" data-i18n="dash.esims.metricSpent">${escapeHtml(t('dash.esims.metricSpent'))}</span>
      <span class="mono">${fmtMoney(spentMicro)}</span>
    </div>
  </div>
  <details class="dash-esim-card__breakdown" data-esim-breakdown="${e.id}">
    <summary data-i18n="dash.esims.breakdownTitle">${escapeHtml(t('dash.esims.breakdownTitle'))}</summary>
    <div class="dash-esim-card__breakdown-tabs" role="tablist">
      <button class="dash-esim-card__breakdown-tab is-active" type="button" data-days="7">7d</button>
      <button class="dash-esim-card__breakdown-tab" type="button" data-days="14">14d</button>
      <button class="dash-esim-card__breakdown-tab" type="button" data-days="30">30d</button>
      <button class="dash-esim-card__breakdown-tab" type="button" data-days="90">90d</button>
    </div>
    <div class="dash-esim-card__breakdown-body" data-esim-breakdown-body>
      <p class="dash-sub" data-i18n="dash.esims.breakdownLoading">${escapeHtml(t('dash.esims.breakdownLoading'))}</p>
    </div>
  </details>
  ${e.status !== 'archived' ? `<footer class="dash-esim-card__actions">
    <button class="btn btn--accent btn--sm" data-esim-action="topup" data-esim-id="${e.id}" data-i18n="dash.esims.topup">${escapeHtml(t('dash.esims.topup'))}</button>
    <button class="btn btn--ghost btn--sm" data-esim-action="qr" data-esim-id="${e.id}" data-i18n="dash.esims.showQr">${escapeHtml(t('dash.esims.showQr'))}</button>
  </footer>` : ''}
</article>`;
}
// Pause/Resume/Cancel buttons removed 2026-05-06: with pay-as-you-go billing
// (no subscription, no recurring charge), pausing has no economic meaning —
// data either has balance or doesn't. Cancel is also moot since unused balance
// stays on the eSIM forever (no refunds, no expiry). The handlers in
// onMount() that listened for these actions are kept harmless: dispatching
// 'pause' / 'cancel' from anywhere else (none today) would still work, just
// the buttons no longer surface them.

export function esimCardHtml(e: api.EsimResp): string {
  return e.status === 'reserved' ? reservedCardHtml(e) : activeCardHtml(e);
}

export function esimsPanel(): string {
  return `
<div class="dash-esim-grid" data-esims-mount>
  <div class="dash-loading" data-i18n="dash.esims.loading">${escapeHtml(t('dash.esims.loading'))}</div>
</div>`;
}


/* ─── Free-trial eSIM (mig 0054) ─────────────────────────────────────────
   Trust before payment: the first eSIM is issued with a small real credit so
   the person can see the service work — install, connect, check the rate —
   and pay only afterwards. Not a giveaway; the copy is deliberately quiet. */
export interface TrialEligibility {
  eligible: boolean;
  reason: 'disabled' | 'anonymous' | 'unverified' | 'already_claimed' | 'has_esim' | null;
  credit_micro_usdt: number;
}

export async function fetchTrialEligibility(): Promise<TrialEligibility | null> {
  try {
    const r = await fetch('/api/me/trial', { credentials: 'include' });
    if (!r.ok) return null;
    return (await r.json()) as TrialEligibility;
  } catch {
    return null;
  }
}

export async function claimTrial(): Promise<{ ok: true; esimId: string } | { ok: false; error: string }> {
  const r = await fetch('/api/me/trial', { method: 'POST', credentials: 'include' });
  const body = (await r.json().catch(() => ({}))) as { esim?: { id: string }; error?: string };
  if (r.ok && body.esim) return { ok: true, esimId: body.esim.id };
  return { ok: false, error: body.error ?? `http_${r.status}` };
}

/**
 * What a refused trial claim says, and whether trying again can help
 * (2026-09-29). Everything but "pool empty" used to read "try again in a
 * minute" and re-enable the button — including "this account already has its
 * eSIM", where no retry ever succeeds. `settled`: the answer will not change,
 * so keep the button off and show the eSIM list instead.
 */
export function trialErrorText(code: string): { text: string; settled: boolean } {
  switch (code) {
    case 'pool_empty': return { text: t('dash.trial.poolEmpty'), settled: false };
    case 'already_claimed':
    case 'has_esim': return { text: t('dash.trial.alreadyHave'), settled: true };
    case 'unverified': return { text: t('dash.trial.unverified'), settled: true };
    case 'disabled': return { text: t('dash.trial.disabledNow'), settled: true };
    case 'cap_reached': return { text: t('topup.errors.generic'), settled: true };
    default: return { text: t('dash.trial.failed'), settled: false };
  }
}

function trialCardHtml(el: TrialEligibility | null): string {
  if (!el) return '';
  if (el.eligible) {
    const credit = `$${(el.credit_micro_usdt / 1_000_000).toFixed(2)}`;
    return `
  <div class="dash-trial" data-trial-card>
    <span class="dash-trial__badge" data-i18n="dash.trial.badge">${escapeHtml(t('dash.trial.badge'))}</span>
    <h3 data-i18n="dash.trial.title">${escapeHtml(t('dash.trial.title'))}</h3>
    <p>${escapeHtml(t('dash.trial.body', { credit }))}</p>
    <div class="dash-trial__actions">
      <button type="button" class="btn btn--accent" data-action="claim-trial" data-i18n="dash.trial.cta">${escapeHtml(t('dash.trial.cta'))}</button>
      <span class="dash-trial__or">${escapeHtml(t('dash.trial.or'))} <a data-action="open-topup" data-i18n="dash.trial.topup">${escapeHtml(t('dash.trial.topup'))}</a></span>
    </div>
    <span class="dash-trial__status" data-trial-status hidden></span>
  </div>`;
  }
  if (el.reason === 'unverified') {
    return `
  <div class="dash-trial dash-trial--locked">
    <span class="dash-trial__badge" data-i18n="dash.trial.badge">${escapeHtml(t('dash.trial.badge'))}</span>
    <h3 data-i18n="dash.trial.title">${escapeHtml(t('dash.trial.title'))}</h3>
    <p data-i18n="dash.trial.unverified">${escapeHtml(t('dash.trial.unverified'))}</p>
    <div class="dash-trial__actions"><a class="btn" href="/dashboard/settings" data-i18n="dash.trial.unverifiedLink">${escapeHtml(t('dash.trial.unverifiedLink'))}</a></div>
  </div>`;
  }
  return '';
}

/**
 * The dashboard header carries a permanent accent "Top up" button. While the
 * free eSIM is the offer, two accent buttons compete and the paid one sits
 * closer to the top — the shape of the first eligible visitor going from
 * signup to a $20 invoice in 18 seconds without claiming.
 *
 * It is a toggle, not a removal, and it is called on BOTH paths: the moment
 * the person has an eSIM the trial is over and "Top up" becomes the primary
 * action again. Demoting it inside the empty-state branch alone would have
 * left the button quiet forever after the trial was claimed, because that
 * branch never runs again.
 */
function setHeaderCtaProminence(offersTrial: boolean): void {
  document.querySelector('.dash-header__cta .btn')?.classList.toggle('btn--accent', !offersTrial);
}

/**
 * Render the eSIM grid. On success: live data, snapshot saved. On
 * network failure (offline): fall back to the last cached snapshot
 * with a clear "as of X minutes ago" badge so the user knows they're
 * not seeing live state. HTTP errors (auth, 5xx) bubble through to
 * the regular error path — those aren't offline.
 */
export async function loadEsimsInto(mount: HTMLElement): Promise<void> {
  const userId = Auth.getUser()?.id ?? '';
  let esims: api.EsimResp[];
  let staleBadge = '';

  try {
    const r = await api.fetchEsims();
    esims = r.esims;
    // Persist for offline next time. Skip if anonymous (shouldn't
    // happen on a route that requires auth, but defensive).
    if (userId) {
      const { saveSnapshot } = await import('../../snapshot');
      saveSnapshot('esims', userId, esims);
    }
  } catch (err) {
    const { isLikelyOffline, getSnapshot, formatAge } = await import('../../snapshot');
    const offline = isLikelyOffline(err);
    const snap = userId ? getSnapshot<api.EsimResp[]>('esims', userId) : null;
    if (offline && snap) {
      esims = snap.data;
      staleBadge = `<div class="dash-stale-badge" role="status" style="grid-column: 1 / -1;">
  <span class="dash-stale-badge__icon" aria-hidden="true">${icon('signal', 14)}</span>
  <span>${escapeHtml(t('dash.esims.staleOfflinePrefix'))} ${escapeHtml(formatAge(snap.ageMs))}. ${escapeHtml(t('dash.esims.staleOfflineHint'))}</span>
</div>`;
    } else {
      // Real error (HTTP 4xx/5xx) or offline with no snapshot. It used to end
      // in the raw "HTTP 500" and offer nothing to do; now it says so plainly
      // and gives a retry (2026-09-29).
      mount.innerHTML = `<div class="dash-empty" style="grid-column: 1 / -1;">
  <p data-i18n="dash.esims.loadFailed">${escapeHtml(t('dash.esims.loadFailed'))}</p>
  <button type="button" class="btn btn--outline" data-esims-retry data-i18n="dash.esims.retry">${escapeHtml(t('dash.esims.retry'))}</button>
</div>`;
      mount.querySelector('[data-esims-retry]')?.addEventListener('click', () => { void loadEsimsInto(mount); }, { once: true });
      return;
    }
  }

  // Hide archived from the grid (the API already filters out 'released').
  const visible = esims.filter((e) => e.status !== 'archived');
  if (visible.length === 0) {
    // Trial first (mig 0054): only asked for on the empty state, only when the
    // operator has it switched on. A network failure degrades to no card.
    // `trialEnabled()`, not `getConfig().trial.enabled`: until /api/config has
    // answered, the default config says "off", and a dashboard that rendered
    // before it answered never offered the free eSIM at all (no listener
    // re-renders this branch). Unknown means ask — the server answers
    // `reason: 'disabled'` when it really is off. Found 2026-09-29.
    const trial = trialEnabled() ? await fetchTrialEligibility() : null;
    const trialCard = trialCardHtml(trial);
    const offersTrial = !!trial?.eligible;
    setHeaderCtaProminence(offersTrial);
    // Onboarding strip — three-step visual walkthrough for first-time
    // users. Lives inside the empty-state container so it auto-disappears
    // the moment user has at least one eSIM (no separate state to track).
    // Operator J-pass 2026-05-09: introduce new users without overwhelming
    // them. Three editorial cards (Top up → Scan QR → Travel) match the
    // /guides phone-mock chassis so users see the same visual language
    // again when they install.
    mount.innerHTML = `${staleBadge}
<div class="dash-empty dash-empty--onboard" style="grid-column: 1 / -1;">
  <h2>${escapeHtml(t('dash.esims.emptyTitle'))}</h2>
  ${offersTrial ? '' : `<p data-i18n="dash.esims.emptyBodyGlobal">${escapeHtml(t('dash.esims.emptyBodyGlobal'))}</p>`}
  ${trialCard}
  <ol class="dash-onboard-strip" aria-label="${escapeHtml(t('dash.onboard.aria'))}">
    <li class="dash-onboard-step">
      <span class="dash-onboard-step__num" aria-hidden="true">01</span>
      <span class="dash-onboard-step__icon" aria-hidden="true">
        <!-- Coin / dollar — engraving feel for "Top up". Monoline, currentColor. -->
        <svg viewBox="0 0 36 36" width="36" height="36" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" role="img">
          <circle cx="18" cy="18" r="13"/>
          <circle cx="18" cy="18" r="10" opacity=".4"/>
          <path d="M21.5 13.4c-.9-1.1-2.2-1.7-3.7-1.7-1.9 0-3.4 1-3.4 2.6 0 1.6 1.7 2.2 3.7 2.7s3.7 1.1 3.7 2.7-1.6 2.6-3.4 2.6c-1.6 0-2.9-.6-3.7-1.7"/>
          <path d="M18 10v2.2M18 23.8V26"/>
        </svg>
      </span>
      <h3 class="dash-onboard-step__title">${escapeHtml(t('dash.onboard.step1.title'))}</h3>
      <p class="dash-onboard-step__body" data-i18n="dash.onboard.step1.body">${escapeHtml(t('dash.onboard.step1.body'))}</p>
    </li>
    <li class="dash-onboard-step">
      <span class="dash-onboard-step__num" aria-hidden="true">02</span>
      <span class="dash-onboard-step__icon" aria-hidden="true">
        <!-- Phone with QR pattern — reads as "scan from your device". -->
        <svg viewBox="0 0 36 36" width="36" height="36" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" role="img">
          <rect x="10" y="3" width="16" height="30" rx="2.6"/>
          <line x1="15.5" y1="29.5" x2="20.5" y2="29.5"/>
          <rect x="13" y="9" width="4" height="4"/>
          <rect x="19" y="9" width="4" height="4"/>
          <rect x="13" y="15" width="4" height="4"/>
          <rect x="19.5" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="22" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="22" width="2" height="2" fill="currentColor"/>
          <rect x="22" y="22" width="2" height="2" fill="currentColor"/>
        </svg>
      </span>
      <h3 class="dash-onboard-step__title">${escapeHtml(t('dash.onboard.step2.title'))}</h3>
      <p class="dash-onboard-step__body" data-i18n="dash.onboard.step2.body">${escapeHtml(t('dash.onboard.step2.body'))}</p>
    </li>
    <li class="dash-onboard-step">
      <span class="dash-onboard-step__num" aria-hidden="true">03</span>
      <span class="dash-onboard-step__icon" aria-hidden="true">
        <!-- Globe with meridians — editorial cartography classic. -->
        <svg viewBox="0 0 36 36" width="36" height="36" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" role="img">
          <circle cx="18" cy="18" r="13"/>
          <ellipse cx="18" cy="18" rx="5.5" ry="13"/>
          <line x1="5" y1="18" x2="31" y2="18"/>
          <path d="M7 10.5c3 1.7 6.7 2.7 11 2.7s8-1 11-2.7" opacity=".55"/>
          <path d="M7 25.5c3-1.7 6.7-2.7 11-2.7s8 1 11 2.7" opacity=".55"/>
        </svg>
      </span>
      <h3 class="dash-onboard-step__title">${escapeHtml(t('dash.onboard.step3.title'))}</h3>
      <p class="dash-onboard-step__body" data-i18n="dash.onboard.step3.body">${escapeHtml(t('dash.onboard.step3.body'))}</p>
    </li>
  </ol>
  ${offersTrial ? '' : `<button class="btn btn--accent" data-action="open-topup" data-i18n="dash.esims.emptyCtaGlobal">${escapeHtml(t('dash.esims.emptyCtaGlobal'))}</button>
  <span class="dash-empty__hint" data-i18n="dash.esims.emptyHint">${escapeHtml(t('dash.esims.emptyHint'))}</span>`}
  <p class="dash-onboard-secondary">
    <a href="/guides">${escapeHtml(t('dash.onboard.guidesLink'))} →</a>
  </p>
</div>`;
    return;
  }
  // At least one eSIM: the trial is behind them, so "Top up" is the primary
  // action again — including right after they claimed the free one.
  setHeaderCtaProminence(false);
  const slotCount = visible.filter((e) => e.status !== 'archived').length;
  // Tile uses `add-new-esim` (not `open-topup`) so the click always opens
  // the CREATE flow regardless of how many active eSIMs the user already
  // has. Operator caught 2026-05-10 ночь +25: «жму Add eSIM — не новую
  // выписываешь, а старую текущую пытаешься пополнить» — admin payments
  // showed 4 TOPUP intents on ESIM 2040400000 (the existing line) instead
  // of CREATE on a fresh ICCID. Root cause: shared `open-topup` action
  // routed to topup-existing when active.length===1 — correct for the
  // header CTA (ambiguous "top up"), wrong for the explicit "+ Add eSIM"
  // tile.
  mount.innerHTML = `${staleBadge}${visible.map(esimCardHtml).join('')}
<button type="button" class="dash-esim-card dash-esim-card--add" data-action="add-new-esim">
  <span class="dash-esim-card__add-icon" aria-hidden="true"><svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 3v16M3 11h16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></span>
  <span data-i18n="dash.esims.addEsim">${escapeHtml(t('dash.esims.addEsim'))}</span>
  <span class="label">${escapeHtml(t('dash.esims.addCount', { n: slotCount }))}</span>
</button>`;
}

/**
 * Tick the countdown labels on every `[data-esim-countdown]` once a second.
 * Returns a dispose handle so the dashboard mount cleanup can stop the timer.
 */
export function startCountdownTicker(root: ParentNode): () => void {
  const tick = (): void => {
    root.querySelectorAll<HTMLElement>('[data-esim-countdown]').forEach((el) => {
      const card = el.closest<HTMLElement>('[data-esim-reserved-until]');
      const until = card?.dataset.esimReservedUntil;
      if (!until) return;
      const remaining = formatRemaining(until);
      el.textContent = remaining
        ? `${t('dash.esims.expiresIn')} ${remaining}`
        : t('dash.esims.reservationExpired');
      // Past the deadline the reservation is released and its eSIM goes back
      // to the pool, but "Pay now" still opened the live invoice. A payment
      // made then is captured with nothing to deliver — a manual refund
      // (payments.ts "Paid after reservation expiry"). Found 2026-09-29.
      if (!remaining) card?.querySelector('.dash-esim-card__pay-now')?.remove();
    });
  };
  const handle = window.setInterval(tick, 1000);
  return () => window.clearInterval(handle);
}

export async function handleEsimAction(action: string, id: string, root: HTMLElement, _btnEl?: HTMLElement | null): Promise<void> {
  const card = root.querySelector<HTMLElement>(`[data-esim-id="${id}"]`);
  const cardButtons = card?.querySelectorAll<HTMLButtonElement>('button[data-esim-action]');
  cardButtons?.forEach((b) => (b.disabled = true));
  if (card) card.style.opacity = '0.6';

  const restore = (): void => {
    cardButtons?.forEach((b) => (b.disabled = false));
    if (card) card.style.opacity = '';
  };

  if (action === 'pause') {
    try { await api.patchEsimStatus(id, 'paused'); showToast(t('toast.esimPaused'), 'success'); }
    catch (err) { showToast((err as Error).message, 'error'); restore(); return; }
  } else if (action === 'resume') {
    try { await api.patchEsimStatus(id, 'active'); showToast(t('toast.esimResumed'), 'success'); }
    catch (err) { showToast((err as Error).message, 'error'); restore(); return; }
  } else if (action === 'cancel') {
    if (!confirm(t('dash.esims.confirmCancel'))) {
      restore();
      return;
    }
    try { await api.cancelEsim(id); showToast(t('toast.esimCancelled'), 'success'); }
    catch (err) { showToast((err as Error).message, 'error'); restore(); return; }
  } else if (action === 'topup') {
    // Open the top-up modal in 'topup' mode for this specific eSIM.
    const { openTopUp } = await import('../../modals/topup');
    const name = card?.querySelector('[data-esim-label-display]')?.firstChild?.textContent?.trim();
    const num = card?.querySelector('.dash-esim-card__id-value')?.textContent?.trim();
    const label = [name, num].filter(Boolean).join(' · ');
    openTopUp({ kind: 'topup', esimId: id, ...(label ? { label } : {}) });
    restore();
    return;
  } else if (action === 'qr') {
    try {
      const r = await fetch(`/api/esims/${id}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = (await r.json()) as { esim: api.EsimResp };
      const { openQrModal } = await import('../../modals/qr');
      openQrModal({
        countryName: t('dash.esims.global'),
        flag: '🌐',
        qrPayload: data.esim.qrPayload ?? '',
        lpaUri: data.esim.lpaUri ?? '',
      });
    } catch (err) {
      showToast(`${t('toast.qrLoadFailed')}: ${(err as Error).message}`, 'error');
    }
    restore();
    return;
  }
  // Refresh list (which replaces this card)
  const mount = root.querySelector<HTMLElement>('[data-esims-mount]');
  if (mount) await loadEsimsInto(mount);
}

export const PENDING_TTL_MS = 5 * 60 * 1000;

export function handleTopUpReturnQuery(): void {
  const qs = new URLSearchParams(window.location.search);
  const status = qs.get('topup');
  if (!status) return;
  qs.delete('topup');
  const cleanSearch = qs.toString();
  const newUrl = window.location.pathname + (cleanSearch ? '?' + cleanSearch : '') + window.location.hash;
  window.history.replaceState({}, '', newUrl);

  // Refresh the eSIM list on BOTH success and cancelled return paths.
  // Operator caught 2026-05-10 ночь: after going to NP and returning,
  // the just-reserved eSIM didn't appear on the dashboard. Cause: the
  // initial dashboard mount fired its loadEsimsInto BEFORE NP's redirect
  // back, so it cached an "old" response (without the reservation). On
  // return we replaceState (no popstate, no re-resolve), so the fresh
  // mount uses the cached snapshot. Force an explicit re-fetch here.
  const mount = document.querySelector<HTMLElement>('[data-esims-mount]');
  if (mount) {
    const refresh = (): void => { void loadEsimsInto(mount); };
    refresh();                              // immediate
    window.setTimeout(refresh, 1000);       // catch IPN-late writes
    if (status === 'success') {
      // For confirmed payments, IPN can lag a few seconds — keep polling
      // so the card flips reserved → active without manual reload.
      window.setTimeout(refresh, 4000);
      window.setTimeout(refresh, 10000);
    }
  }

  if (status === 'success') {
    showToast(t('toast.topupSuccess'), 'success');
  } else if (status === 'cancelled') {
    showToast(t('toast.topupCancelled'), 'info');
  }
}

/**
 * Resume a pending eSIM purchase if one was stashed before the user signed
 * in. The country page's "Top up" button stashes the slug in sessionStorage.
 * After login we open the top-up modal pre-filled with that country chip.
 */
export async function resumePendingEsim(): Promise<void> {
  const raw = sessionStorage.getItem('roamzy:pending-esim-slug');
  if (!raw) return;
  let slug: string | null = null;
  try {
    const parsed = JSON.parse(raw) as { slug?: string; ts?: number };
    if (parsed && typeof parsed.slug === 'string' && typeof parsed.ts === 'number') {
      if (Date.now() - parsed.ts > PENDING_TTL_MS) {
        sessionStorage.removeItem('roamzy:pending-esim-slug');
        return;
      }
      slug = parsed.slug;
    }
  } catch {
    slug = raw;
  }
  if (!slug) return;
  sessionStorage.removeItem('roamzy:pending-esim-slug');

  // Single-reservation invariant (operator-set 2026-05-11). If the user
  // already has a reserved eSIM (e.g. started reservation on device A,
  // logged in on device B), don't double-reserve — bounce them to the
  // existing Pay-now card. Helper handles toast + navigation.
  const { assertNoActiveReservation } = await import('../../reservation-guard');
  if (!(await assertNoActiveReservation())) return;

  const { getCountryBySlug } = await import('../../data/prices');
  const country = getCountryBySlug(slug);
  const { openTopUp } = await import('../../modals/topup');
  openTopUp({
    kind: 'create',
    countrySlug: slug,
    ...(country ? { countryName: countryDisplayName(country), flag: country.flag } : {}),
  });
}
